import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Inbox,
  Workflow,
  Bot,
  UserCircle2,
  FlaskConical,
  ScrollText,
  BookOpen,
  Boxes,
  Sparkles,
  Library,
  MessagesSquare,
  CalendarClock,
  Antenna,
  GitPullRequestArrow,
  ListTodo,
  Cpu,
  Brain,
  Users,
  BarChart3,
  Plug,
  Network,
  Settings as SettingsIcon,
  Github as GithubIcon,
  CheckSquare,
  Gamepad2,
  Slack as SlackIcon,
} from "lucide-react";

import MissionControl from "./MissionControl";
import Visualize from "./Visualize";
import Analytics from "./Analytics";
import Requests from "./Requests";
import Routes from "./Routes";
import Channels from "./Channels";
import Github from "./Github";
import Asana from "./Asana";
import Slack from "./Slack";
import Agents from "./Agents";
import Personas from "./Personas";
import Mcp from "./Mcp";
import Simulator from "./Simulator";
import Logs from "./Logs";
import Journals from "./Journals";
import Spawns from "./Spawns";
import Jobs from "./Jobs";
import Delegations from "./Delegations";
import Tasks from "./Tasks";
import Memory from "./Memory";
import Contacts from "./Contacts";
import Skills from "./Skills";
import Catalog from "./Catalog";
import BusInbox from "./BusInbox";
import ScheduledTasks from "./ScheduledTasks";
import Wiki from "./Wiki";
import Settings from "./Settings";

export type PageId =
  | "mission"
  | "visualize"
  | "analytics"
  | "requests"
  | "channels"
  | "github"
  | "asana"
  | "slack"
  | "routes"
  | "agents"
  | "personas"
  | "mcp"
  | "simulator"
  | "logs"
  | "journals"
  | "spawns"
  | "jobs"
  | "delegations"
  | "tasks"
  | "wiki"
  | "memory"
  | "contacts"
  | "skills"
  | "catalog"
  | "bus-inbox"
  | "bus-scheduled"
  | "settings";

export interface PageDef {
  id: PageId;
  label: string;
  icon: LucideIcon;
  group: "overview" | "operations" | "configuration" | "knowledge" | "library" | "system";
  Component: React.ComponentType;
}

export const pages: PageDef[] = [
  { id: "mission", label: "Mission Control", icon: LayoutDashboard, group: "overview", Component: MissionControl },
  { id: "visualize", label: "Visualize", icon: Gamepad2, group: "overview", Component: Visualize },

  { id: "analytics", label: "Analytics", icon: BarChart3, group: "operations", Component: Analytics },
  { id: "requests", label: "Requests", icon: Inbox, group: "operations", Component: Requests },
  { id: "bus-inbox", label: "Bus Inbox", icon: MessagesSquare, group: "operations", Component: BusInbox },
  { id: "spawns", label: "Spawns", icon: Sparkles, group: "operations", Component: Spawns },
  { id: "jobs", label: "Jobs", icon: Cpu, group: "operations", Component: Jobs },
  { id: "delegations", label: "Delegations", icon: GitPullRequestArrow, group: "operations", Component: Delegations },
  { id: "tasks", label: "Tasks", icon: ListTodo, group: "operations", Component: Tasks },
  { id: "logs", label: "Logs", icon: ScrollText, group: "operations", Component: Logs },

  { id: "channels", label: "Channels", icon: Antenna, group: "configuration", Component: Channels },
  { id: "github", label: "GitHub", icon: GithubIcon, group: "configuration", Component: Github },
  { id: "asana", label: "Asana", icon: CheckSquare, group: "configuration", Component: Asana },
  { id: "slack", label: "Slack", icon: SlackIcon, group: "configuration", Component: Slack },
  { id: "routes", label: "Routes", icon: Workflow, group: "configuration", Component: Routes },
  { id: "agents", label: "Agents", icon: Bot, group: "configuration", Component: Agents },
  { id: "personas", label: "Personas", icon: UserCircle2, group: "configuration", Component: Personas },
  { id: "mcp", label: "MCP", icon: Plug, group: "configuration", Component: Mcp },
  { id: "bus-scheduled", label: "Schedules", icon: CalendarClock, group: "configuration", Component: ScheduledTasks },
  { id: "simulator", label: "Simulator", icon: FlaskConical, group: "configuration", Component: Simulator },

  { id: "wiki", label: "Wiki", icon: Network, group: "knowledge", Component: Wiki },
  { id: "memory", label: "Memory", icon: Brain, group: "knowledge", Component: Memory },
  { id: "contacts", label: "Contacts", icon: Users, group: "knowledge", Component: Contacts },
  { id: "journals", label: "Journals", icon: BookOpen, group: "knowledge", Component: Journals },

  { id: "skills", label: "Skills", icon: Boxes, group: "library", Component: Skills },
  { id: "catalog", label: "Catalog", icon: Library, group: "library", Component: Catalog },

  { id: "settings", label: "Settings", icon: SettingsIcon, group: "system", Component: Settings },
];

export const pagesById: Record<PageId, PageDef> = pages.reduce(
  (acc, p) => {
    acc[p.id] = p;
    return acc;
  },
  {} as Record<PageId, PageDef>,
);

// Intent-based sections. `overview` (Mission Control) and `system` (Settings)
// are pinned, label-less single-item groups — the rail skips the header for an
// empty label, so they read as standalone top/bottom items.
export const groups: Array<{ id: PageDef["group"]; label: string }> = [
  { id: "overview", label: "" },
  { id: "operations", label: "Operations" },
  { id: "configuration", label: "Configuration" },
  { id: "knowledge", label: "Knowledge" },
  { id: "library", label: "Library" },
  { id: "system", label: "" },
];

export function getPageFromHash(): PageId {
  // Tolerate a query suffix ("#/spawns?persona=alfred") — deep links from the
  // Visualize dialog carry an initial filter.
  const hash = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  return (pagesById as Record<string, PageDef>)[hash] ? (hash as PageId) : "mission";
}

/** Read a query param from the hash ("#/spawns?persona=alfred"). Pages read
 *  this once on mount as their initial filter/selection. */
export function getHashParam(key: string): string | null {
  const q = window.location.hash.split("?")[1];
  return q ? new URLSearchParams(q).get(key) : null;
}

export function setPageHash(id: PageId): void {
  window.location.hash = `/${id}`;
}
